import _defineProperty from "/builds/vtblife/frontend/common/gatsby-party/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/builds/vtblife/frontend/common/gatsby-party/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/builds/vtblife/frontend/common/gatsby-party/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/builds/vtblife/frontend/common/gatsby-party/node_modules/@babel/runtime/regenerator/index.js";
import { __awaiter } from "tslib";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import useOnClickOutside from '../../hooks/use-on-click-outside';
import { directionAlias, toClickOutside } from './utils';
import { arrow, offset, overflow, adaptiveDirection, silent, createPopper } from './popper';
import s from './tooltip.module.css';
import useTransitionState from '../../hooks/use-transition-state';
import useDOMRoot from '../../hooks/use-dom-root';
var closingTimeout = 240;
var Launchpad = function Launchpad(_ref) {
  var root = _ref.root,
    children = _ref.children;
  return root ? createPortal(children, root) : children;
};
export var Tooltip = function Tooltip(_ref2) {
  var _ref2$to = _ref2.to,
    to = _ref2$to === void 0 ? {
      current: new Set()
    } : _ref2$to,
    _ref2$container = _ref2.container,
    container = _ref2$container === void 0 ? {
      current: null
    } : _ref2$container,
    _ref2$trigger = _ref2.trigger,
    trigger = _ref2$trigger === void 0 ? 'hover' : _ref2$trigger,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'black' : _ref2$color,
    _ref2$direction = _ref2.direction,
    direction = _ref2$direction === void 0 ? 'up' : _ref2$direction,
    _ref2$placement = _ref2.placement,
    placement = _ref2$placement === void 0 ? 'local' : _ref2$placement,
    _ref2$arrow = _ref2.arrow,
    showArrow = _ref2$arrow === void 0 ? true : _ref2$arrow,
    children = _ref2.children,
    _ref2$enableAnimation = _ref2.enableAnimation,
    enableAnimation = _ref2$enableAnimation === void 0 ? true : _ref2$enableAnimation;
  var _a;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    visible = _useState2[0],
    setVisible = _useState2[1];
  var tooltip = useRef(null);
  var event = useRef();
  var _useTransitionState = useTransitionState(visible, {
      duration: closingTimeout
    }),
    _useTransitionState2 = _slicedToArray(_useTransitionState, 2),
    state = _useTransitionState2[0],
    close = _useTransitionState2[1];
  var root = useDOMRoot({
    anchor: 'tooltip-launchpad',
    style: s.root
  });
  var popper = useRef(null);
  var show = useCallback(function (evt) {
    return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!visible) {
              _context.next = 2;
              break;
            }
            return _context.abrupt("return");
          case 2:
            event.current = evt;
            setVisible(true);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
  }, [visible]);
  var hide = useCallback(function () {
    return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            if (!(visible && state === 'show')) {
              _context2.next = 3;
              break;
            }
            _context2.next = 3;
            return close();
          case 3:
            setVisible(false);
          case 4:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
  }, [visible, state, close]);
  useEffect(function () {
    var _a;
    if (!to.current || state !== 'appear' || !event.current) {
      return;
    }
    var ref = (trigger === 'click' || trigger === 'static') && Array.from(to.current).find(function (el) {
      var _a;
      return el.contains((_a = event.current) === null || _a === void 0 ? void 0 : _a.target);
    }) || event.current.target;
    if (popper.current) {
      (_a = popper.current) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    popper.current = createPopper(ref, tooltip.current, {
      placement: directionAlias[direction],
      modifiers: [].concat(_toConsumableArray(showArrow ? [arrow] : []), [offset, overflow(container.current || document.body), adaptiveDirection(container.current, direction)])
    });
  }, [state, to, direction, trigger, container, showArrow]);
  useEffect(function () {
    var _a;
    if (state !== 'idle') {
      return;
    }
    (_a = popper.current) === null || _a === void 0 ? void 0 : _a.setOptions({
      modifiers: _toConsumableArray(silent)
    });
  }, [state]);
  useOnClickOutside(toClickOutside(popper), hide, trigger === 'click');
  useEffect(function () {
    var controls = to.current;
    if (!controls || controls.size === 0) {
      return;
    }
    if (trigger === 'static') {
      controls.forEach(function (item) {
        show({
          target: item
        });
      });
      return;
    }
    var handlerType = trigger === 'click' ? 'click' : 'mouseover';
    controls.forEach(function (item) {
      item.addEventListener(handlerType, show);
      if (trigger === 'hover') {
        item.addEventListener('mouseleave', hide);
      }
    });
    return function () {
      controls.forEach(function (item) {
        item.removeEventListener(handlerType, show);
        item.removeEventListener('mouseleave', hide);
      });
    };
  }, [trigger, (_a = to.current) === null || _a === void 0 ? void 0 : _a.size]);
  if (state === 'init' || state === 'idle') {
    return null;
  }
  return React.createElement(Launchpad, {
    root: placement === 'body' ? root : null
  }, React.createElement("div", {
    ref: tooltip,
    className: classNames(s.content, _defineProperty({}, s.animation, enableAnimation), s[color], state === 'show' && s.visible, placement === 'local' && s.local)
  }, children, showArrow && React.createElement("div", {
    className: s.arrow,
    "data-popper-arrow": true
  })));
};